import React, {
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { getAspectRatio } from 'utils/getAspectRatio'
import { CompanyContext } from './CompanyContext'
import MediaFilters from 'components/layout/MediaFilters'
import { SessionContext } from './SessionContext'
import {
    useAssetsInfinite,
    useDeleteAsset,
} from 'api/hooks/useAssets'
import { queryKeys } from 'api/queryKeys'

/**
 * Thin compatibility shim. Filter drawer UI state stays local; asset fetching
 * runs through the new infinite query (link-based JSON:API pagination + RQ
 * cache). The post-fetch enrichment (src/original/aspectRatio) is preserved.
 */

const MediaContext = React.createContext({
    myMedia: [],
    updateMyMedia: () => {},
    deleteAssetMyMedia: () => {},
    isLoadingMyMedia: false,
    filtersMyMedia: [],
    setFiltersMyMedia: () => {},
    filtersMyMediaDrawerOpened: false,
    setFiltersMyMediaDrawerOpened: () => {},
    loadMoreMyMedia: () => {},
    hasMoreMyMedia: false,
    totalMyMedia: 0,
})

function buildAssetFilter(filtersMyMedia) {
    const filter = {}
    if (filtersMyMedia?.media_types?.length) {
        filter.type = filtersMyMedia.media_types
            .map((e) => e.value)
            .join(',')
    } else {
        filter.type = 'image,video'
    }
    if (filtersMyMedia?.query) filter.text = filtersMyMedia.query
    if (
        filtersMyMedia?.create_date_range &&
        filtersMyMedia.create_date_range.length > 0
    ) {
        filter.created_at_after = new Date(
            filtersMyMedia.create_date_range[0]
        ).toDateString()
        filter.created_at_before = new Date(
            filtersMyMedia.create_date_range[1]
        ).toDateString()
    }
    if (filtersMyMedia?.users && filtersMyMedia.users.length > 0) {
        filter.user_id = filtersMyMedia.users.map((e) => e.value).join(',')
    }
    if (
        filtersMyMedia?.social_networks &&
        filtersMyMedia.social_networks.length > 0
    ) {
        filter.social_network_type_with_children =
            filtersMyMedia.social_networks.map((e) => e.value).join(',')
    }
    if (filtersMyMedia?.unused_assets) filter.has_relation = '!posts'
    if (filtersMyMedia?.folder_id !== undefined) {
        filter.folder_id = filtersMyMedia.folder_id
    }
    return filter
}

const ASSET_INCLUDE = [
    'posts',
    'image',
    'image.thumbnail',
    'video',
    'video.thumbnail',
    'user',
    'children.user',
    'children.user.image',
    'user.image',
    'children.image',
    'children.video',
]

const enrichAsset = (item) => {
    const src =
        item?.image?.thumbnail?.url || item?.image?.url || item?.video?.url
    const original = item?.image?.url || item?.video?.url
    const asset = item?.image || item?.video
    return {
        ...item,
        src,
        original,
        aspectRatio: getAspectRatio(asset?.width, asset?.height),
    }
}

const MediaProvider = ({ children }) => {
    const { user } = useContext(SessionContext)
    const { company } = useContext(CompanyContext)
    const qc = useQueryClient()
    const [filtersMyMedia, setFiltersMyMedia] = useState({})
    const [filtersMyMediaDrawerOpened, setFiltersMyMediaDrawerOpened] =
        useState(false)

    const apiFilter = useMemo(
        () => buildAssetFilter(filtersMyMedia),
        [filtersMyMedia]
    )

    const {
        data,
        isLoading: isLoadingMyMedia,
        isFetchingNextPage,
        hasNextPage,
        fetchNextPage,
        refetch,
    } = useAssetsInfinite(
        {
            companyId: company?.id,
            sort: '-created_at',
            include: ASSET_INCLUDE,
            filter: apiFilter,
        },
        { enabled: !!company?.id }
    )

    const myMedia = useMemo(() => {
        if (!data?.pages?.length) return []
        return data.pages.flatMap((p) => (p?.data ?? []).map(enrichAsset))
    }, [data?.pages])

    const totalMyMedia = useMemo(
        () => data?.pages?.[0]?.meta?.total || 0,
        [data?.pages]
    )

    // Push total → Intercom (legacy behavior)
    useEffect(() => {
        if (totalMyMedia) {
            try {
                window.Intercom?.('update', {
                    media_quantity: totalMyMedia,
                })
            } catch (_) {}
        }
    }, [totalMyMedia])

    const updateMyMedia = useCallback(
        (loadMore = false) => {
            if (loadMore) {
                if (hasNextPage && !isFetchingNextPage) fetchNextPage()
            } else {
                refetch()
            }
        },
        [hasNextPage, isFetchingNextPage, fetchNextPage, refetch]
    )

    const loadMoreMyMedia = useCallback(() => {
        if (hasNextPage && !isFetchingNextPage) fetchNextPage()
    }, [hasNextPage, isFetchingNextPage, fetchNextPage])

    const clearMyMedia = useCallback(() => {
        qc.removeQueries({ queryKey: queryKeys.assets.all })
    }, [qc])

    const { mutateAsync: deleteAssetMutate } = useDeleteAsset()
    const deleteAssetMyMedia = useCallback(
        async (id) => {
            await deleteAssetMutate(id)
        },
        [deleteAssetMutate]
    )

    // Persist filters per user (parity with legacy behavior).
    useEffect(() => {
        if (!user?.id) return
        try {
            const cleaned = Object.fromEntries(
                Object.entries(filtersMyMedia).filter(([_, v]) => !!v)
            )
            if (
                Object.keys(filtersMyMedia).length !==
                Object.keys(cleaned).length
            ) {
                setFiltersMyMedia(cleaned)
            }
            localStorage.setItem(
                `${user.id}_filtersMyMedia`,
                JSON.stringify(filtersMyMedia)
            )
        } catch (_) {}
    }, [filtersMyMedia, user?.id])

    useEffect(() => {
        if (!user?.id) return
        try {
            const stored = localStorage.getItem(`${user.id}_filtersMyMedia`)
            if (stored) {
                const parsed = JSON.parse(stored)
                if (parsed) setFiltersMyMedia(parsed)
            }
        } catch (_) {}
    }, [user?.id])

    const value = useMemo(
        () => ({
            myMedia,
            updateMyMedia,
            clearMyMedia,
            deleteAssetMyMedia,
            isLoadingMyMedia,
            filtersMyMedia,
            setFiltersMyMedia,
            filtersMyMediaDrawerOpened,
            setFiltersMyMediaDrawerOpened,
            loadMoreMyMedia,
            hasMoreMyMedia: !!hasNextPage,
            totalMyMedia,
        }),
        [
            myMedia,
            updateMyMedia,
            clearMyMedia,
            deleteAssetMyMedia,
            isLoadingMyMedia,
            filtersMyMedia,
            filtersMyMediaDrawerOpened,
            loadMoreMyMedia,
            hasNextPage,
            totalMyMedia,
        ]
    )

    return (
        <MediaContext.Provider value={value}>
            {children}
            {filtersMyMediaDrawerOpened && <MediaFilters />}
        </MediaContext.Provider>
    )
}

export { MediaContext, MediaProvider }
